import React, { useState, useEffect } from 'react';
import { X, Image, Save, Loader, Disc3 } from 'lucide-react';
import FileUpload from './FileUpload';
import ContributingArtistSelector from './ContributingArtistSelector';
import PrivacySettings from './PrivacySettings';
import CopyrightSettings from './CopyrightSettings';
import MyMediaSelector from './MyMediaSelector';
import api from '../services/api';

const emptyMetadata = {
    title: '',
    description: '',
    accessLevel: 'public',
    accessPassword: '',
    accessHint: '',
    isPaid: false,
    price: '',
    currency: 'USD',
    isCopyrighted: false,
    copyrightText: ''
};

// Handles both creating a new album and editing an existing one (keyed by
// `editingAlbum`), modeled on MusicUploadModal.jsx's metadata step — but an
// album has no underlying file, only an optional cover image, so it skips
// straight to the metadata form (no upload step).
const AlbumFormModal = ({ isOpen, onClose, onSuccess, editingAlbum = null }) => {
    const [metadata, setMetadata] = useState(emptyMetadata);
    const [coverImage, setCoverImage] = useState(null);
    const [collaborators, setCollaborators] = useState([]);
    const [taggedMedia, setTaggedMedia] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingExisting, setLoadingExisting] = useState(false);

    const isEditing = !!editingAlbum;

    useEffect(() => {
        if (!isOpen) return;

        if (editingAlbum) {
            setMetadata({
                title: editingAlbum.title || '',
                description: editingAlbum.description || '',
                accessLevel: editingAlbum.access_level || 'public',
                accessPassword: editingAlbum.access_password || '',
                accessHint: editingAlbum.access_hint || '',
                isPaid: editingAlbum.access_level === 'paid' || parseFloat(editingAlbum.price) > 0,
                price: editingAlbum.price || '',
                currency: editingAlbum.currency || 'USD',
                isCopyrighted: !!editingAlbum.is_copyrighted,
                copyrightText: editingAlbum.copyright_text || ''
            });
            setCoverImage(editingAlbum.cover_image_url ? { url: editingAlbum.cover_image_url } : null);

            const loadExisting = async () => {
                setLoadingExisting(true);
                try {
                    const [collabRes, itemsRes] = await Promise.all([
                        api.get(`/contributing-artists/album/${editingAlbum.id}`),
                        api.get(`/albums/${editingAlbum.id}/items`, { params: { limit: 100 } })
                    ]);
                    if (collabRes.data.success) {
                        setCollaborators(collabRes.data.data.map(c => ({
                            user_id: c.artist_user_id,
                            username: c.username,
                            display_name: c.display_name,
                            profile_picture_url: c.profile_picture_url
                        })));
                    }
                    if (itemsRes.data.success) {
                        setTaggedMedia(itemsRes.data.data);
                    }
                } catch (err) {
                    console.error('Error loading existing album details:', err);
                } finally {
                    setLoadingExisting(false);
                }
            };
            loadExisting();
        } else {
            setMetadata(emptyMetadata);
            setCoverImage(null);
            setCollaborators([]);
            setTaggedMedia([]);
        }
    }, [isOpen, editingAlbum]);

    const handleMetadataChange = (field, value) => {
        setMetadata(prev => ({ ...prev, [field]: value }));
    };

    const handleClose = () => {
        setMetadata(emptyMetadata);
        setCoverImage(null);
        setCollaborators([]);
        setTaggedMedia([]);
        onClose();
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const payload = {
                title: metadata.title,
                description: metadata.description,
                accessLevel: metadata.accessLevel,
                accessPassword: metadata.accessPassword,
                accessHint: metadata.accessHint,
                price: metadata.isPaid ? metadata.price : '',
                currency: metadata.currency,
                isCopyrighted: metadata.isCopyrighted,
                copyrightText: metadata.isCopyrighted ? metadata.copyrightText : ''
            };
            if (coverImage?.optimizedUrl || coverImage?.url) {
                payload.cover_image_url = coverImage.optimizedUrl || coverImage.url;
            }

            let albumId = editingAlbum?.id;
            if (isEditing) {
                await api.put(`/albums/${albumId}`, payload);
            } else {
                const response = await api.post('/albums', payload);
                albumId = response.data.data.id;
            }

            await api.post(`/contributing-artists/album/${albumId}`, {
                contributingArtistIds: collaborators.map(c => c.user_id)
            }).catch(err => console.error('Error saving album collaborators:', err));

            await api.post(`/albums/${albumId}/media`, {
                uploadIds: taggedMedia.map(m => m.id)
            }).catch(err => console.error('Error saving album media:', err));

            onSuccess?.();
            handleClose();
        } catch (error) {
            console.error('Error saving album:', error);
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
            <div className="bg-gray-900 rounded-lg border border-gray-700 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between p-6 border-b border-gray-700">
                    <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                        <Disc3 className="w-6 h-6 text-purple-400" />
                        {isEditing ? 'Edit Album' : 'Create Album'}
                    </h2>
                    <button
                        onClick={handleClose}
                        aria-label="Close modal"
                        className="w-8 h-8 rounded-lg bg-gray-800 hover:bg-gray-700 flex items-center justify-center transition-colors"
                    >
                        <X className="w-4 h-4 text-gray-400" />
                    </button>
                </div>

                <div className="p-6 space-y-6">
                    {loadingExisting ? (
                        <div className="text-center py-8">
                            <Loader className="w-6 h-6 animate-spin text-purple-500 mx-auto" />
                        </div>
                    ) : (
                        <>
                            {/* Cover Image */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-3">Main Image</label>
                                <div className="flex items-start gap-4">
                                    <div className="flex-shrink-0">
                                        <div className="w-24 h-24 bg-gray-800 rounded-lg border-2 border-dashed border-gray-600 flex items-center justify-center overflow-hidden">
                                            {coverImage ? (
                                                <img
                                                    src={coverImage.optimizedUrl || coverImage.url}
                                                    alt="Cover"
                                                    width="96"
                                                    height="96"
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <Image className="w-8 h-8 text-gray-400" />
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <FileUpload
                                            type="image"
                                            onUpload={setCoverImage}
                                            onError={(error) => console.error('Cover upload error:', error)}
                                            maxSizeMB={5}
                                            accept="image/*"
                                            className="h-24"
                                            compact
                                            showPreview={false}
                                        >
                                            <span className="text-purple-400">Upload cover image</span> (optional)
                                        </FileUpload>
                                    </div>
                                </div>
                            </div>

                            {/* Title */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">Album Title *</label>
                                <input
                                    type="text"
                                    value={metadata.title}
                                    onChange={(e) => handleMetadataChange('title', e.target.value)}
                                    className="input-field"
                                    placeholder="Enter album title"
                                    maxLength="255"
                                    required
                                />
                            </div>

                            {/* Description */}
                            <div>
                                <label className="block text-sm font-semibold text-gray-300 mb-2">Description</label>
                                <textarea
                                    value={metadata.description}
                                    onChange={(e) => handleMetadataChange('description', e.target.value)}
                                    className="input-field min-h-[100px] resize-vertical"
                                    placeholder="Tell people about this album..."
                                    rows={4}
                                />
                            </div>

                            {/* Media in this album */}
                            <MyMediaSelector
                                selectedMedia={taggedMedia}
                                onMediaChange={setTaggedMedia}
                                disabled={loading}
                            />

                            {/* Access Control */}
                            <PrivacySettings
                                value={metadata.accessLevel}
                                password={metadata.accessPassword}
                                passwordHint={metadata.accessHint}
                                isPaid={metadata.isPaid}
                                price={metadata.price}
                                currency={metadata.currency}
                                onChange={(v) => handleMetadataChange('accessLevel', v)}
                                onPasswordChange={(v) => handleMetadataChange('accessPassword', v)}
                                onHintChange={(v) => handleMetadataChange('accessHint', v)}
                                onIsPaidChange={(v) => handleMetadataChange('isPaid', v)}
                                onPriceChange={(v) => handleMetadataChange('price', v)}
                                onCurrencyChange={(v) => handleMetadataChange('currency', v)}
                                disabled={loading}
                            />

                            {/* Copyright */}
                            <CopyrightSettings
                                isCopyrighted={metadata.isCopyrighted}
                                copyrightText={metadata.copyrightText}
                                onIsCopyrightedChange={(v) => handleMetadataChange('isCopyrighted', v)}
                                onCopyrightTextChange={(v) => handleMetadataChange('copyrightText', v)}
                                disabled={loading}
                            />

                            {/* Collaborators */}
                            <ContributingArtistSelector
                                selectedArtists={collaborators}
                                onArtistsChange={setCollaborators}
                                placeholder="Search for collaborators on this album..."
                                maxArtists={10}
                                disabled={loading}
                            />

                            {/* Action Buttons */}
                            <div className="flex items-center justify-end pt-4 border-t border-gray-700">
                                <button
                                    onClick={handleSave}
                                    disabled={
                                        !metadata.title.trim() ||
                                        loading ||
                                        ((metadata.accessLevel === 'password_protected' || metadata.accessLevel === 'secret') && !metadata.accessPassword.trim()) ||
                                        (metadata.isPaid && (!metadata.price || parseFloat(metadata.price) <= 0)) ||
                                        (metadata.isCopyrighted && !metadata.copyrightText.trim())
                                    }
                                    className="btn-primary flex items-center gap-2 disabled:opacity-50"
                                >
                                    {loading ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        <>
                                            <Save className="w-4 h-4" />
                                            {isEditing ? 'Save Changes' : 'Create Album'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default AlbumFormModal;
